import React, { useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Icons,
  IconButton,
  Checkbox,
} from '@ublend-npm/aulaui-core';
import { useDispatch, useSelector } from 'react-redux';
import {
  fetchHomePosts,
  pollNewHomePosts,
} from '../../../../../actions/home/posts';
import InfiniteScroller from '../../../../containers/common/InfiniteScroller';
import { useTheme } from '@ublend-npm/aulaui-core';
import { Post } from '@app/types/core';
import useBreakpoints from '@app/hooks/useBreakpoints';
import RecentUpdatesSkeleton, {
  RecentUpdateCardSkeleton,
} from './RecentUpdatesSkeleton';
import RecentUpdateCard from './RecentUpdateCard';
import RecentUpdatesInfoPopup from './RecentUpdatesInfoPopup';
import PostDetailPopup from './PostDetailPopup';
import { getHomeViewRole } from '@app/selectors/home';
import useTrackHomeWidgetLoaded from '../common/useTrackHomeWidgetLoaded';

const POLL_INTERVAL_MS = 30_000;

const RecentUpdates = () => {
  const dispatch = useDispatch();
  const [isInfoOpen, setIsInfoOpen] = React.useState(false);
  const [selectedPost, setSelectedPost] = React.useState<any>(null);

  const { posts, postsLoading, postsHasMore, educatorPosts } = useSelector(
    (state: any) => state.home || {}
  );

  const location = useSelector(
    (state: any) => state.routing?.locationBeforeTransitions
  );

  // Extract postId from URL if we are on /home/post/:postId
  const match = location?.pathname?.match(/\/home\/post\/(.+)/);
  const routePostId = match ? match[1] : null;

  useEffect(() => {
    if (routePostId && !postsLoading && posts?.length > 0) {
      const post = posts.find(
        (p: any) => p.objectId === routePostId || p.id === routePostId
      ) || { id: routePostId, objectId: routePostId };
      if (
        !selectedPost ||
        (selectedPost.objectId !== routePostId &&
          selectedPost.id !== routePostId)
      ) {
        setSelectedPost(post);
      }
    } else if (!routePostId && selectedPost) {
      setSelectedPost(null);
    }
  }, [routePostId, posts, selectedPost, postsLoading]);

  const handleClosePost = React.useCallback(() => {
    dispatch({
      type: '@@router/CALL_HISTORY_METHOD',
      payload: {
        method: 'push',
        args: ['/home'],
      },
    } as any);
  }, [dispatch]);
  const activeRole = useSelector(getHomeViewRole);
  const prevRoleRef = useRef(activeRole);

  const theme = useTheme();
  const { md: isSidebarLayout } = useBreakpoints();
  const isVerticalFeed = isSidebarLayout;
  const isHorizontalFeed = !isVerticalFeed;

  useTrackHomeWidgetLoaded(
    'recent_updates',
    !postsLoading && (posts.length > 0 || !postsHasMore),
    posts.length
  );

  useEffect(() => {
    if (prevRoleRef.current !== activeRole) {
      prevRoleRef.current = activeRole;
      dispatch(fetchHomePosts(true, educatorPosts, activeRole) as any);
      return;
    }

    // Only fetch initially if empty and has more
    if (posts.length === 0 && postsHasMore && !postsLoading) {
      dispatch(fetchHomePosts(true, educatorPosts, activeRole) as any);
    }
  }, [
    dispatch,
    posts.length,
    postsHasMore,
    postsLoading,
    educatorPosts,
    activeRole,
  ]);

  // Poll for new posts every 30 seconds once we have an initial batch
  useEffect(() => {
    if (posts.length === 0) return;

    const intervalId = setInterval(() => {
      dispatch(pollNewHomePosts() as any);
    }, POLL_INTERVAL_MS);

    return () => clearInterval(intervalId);
  }, [dispatch, posts.length > 0, activeRole, educatorPosts]);

  const loadMorePosts = () => {
    dispatch(fetchHomePosts(false, educatorPosts, activeRole) as any);
  };

  const feedHeight = isSidebarLayout ? '100%' : '390px';

  return (
    <Box
      component="section"
      aria-labelledby="recent-updates-title"
      sx={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        width: '100%',
        maxWidth: '100%',
        minWidth: 0,
        minHeight: 0,
        height: isSidebarLayout ? '100%' : 'auto',
        maxHeight: isSidebarLayout ? '100%' : 'none',
        overflow: isSidebarLayout ? 'hidden' : 'visible',
        boxSizing: 'border-box',
      }}
    >
      <Box sx={{ flexShrink: 0 }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            gap: 1,
            mb: 0.5,
          }}
        >
          <Typography
            variant="subtitle1"
            fontWeight="medium"
            color="primary"
            id="recent-updates-title"
          >
            Recent updates
          </Typography>
          <IconButton
            onClick={() => setIsInfoOpen(true)}
            aria-label="Recent updates information"
            size="small"
            sx={{
              color: theme.palette.text.secondary,
              '&:hover': {
                color: theme.palette.primary.main,
              },
            }}
          >
            <Icons.InfoOutlined fontSize="small" />
          </IconButton>
        </Box>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'flex-start',
            alignItems: 'center',
            gap: 2,
          }}
        >
          <Typography
            color="textPrimary"
            variant="body1"
            id="educators-only-label"
          >
            Show educators post only
          </Typography>
          <Checkbox
            kind="switch"
            checked={!!educatorPosts}
            onChange={() =>
              dispatch(fetchHomePosts(true, !educatorPosts, activeRole) as any)
            }
            aria-label="show educators post only"
            inputProps={{
              id: 'educators-only-switch',
              'aria-labelledby': 'educators-only-label',
            }}
          />
        </Box>
      </Box>
      <Box
        sx={{
          flex: isSidebarLayout ? 1 : '0 0 auto',
          width: '100%',
          maxWidth: '100%',
          minWidth: 0,
          minHeight: isSidebarLayout ? 0 : undefined,
          display: 'flex',
          flexDirection: 'column',
          overflow: isHorizontalFeed
            ? 'hidden'
            : isSidebarLayout
            ? 'hidden'
            : 'visible',
        }}
      >
        {posts.length === 0 && postsLoading ? (
          <Box
            role="status"
            aria-label="Loading recent updates"
            aria-busy="true"
          >
            <RecentUpdatesSkeleton
              orientation={isVerticalFeed ? 'vertical' : 'horizontal'}
            />
          </Box>
        ) : posts.length === 0 ? (
          <Box
            sx={{
              display: 'flex',
              flexDirection: isVerticalFeed ? 'column' : 'row',
              gap: 2,
              width: '100%',
              maxWidth: '100%',
              minWidth: 0,
              height: isSidebarLayout ? '100%' : '365px',
              overflowX: isHorizontalFeed ? 'auto' : 'visible',
              overflowY: isSidebarLayout ? 'auto' : 'visible',
              pb: isVerticalFeed ? 0 : 2,
              flex: isSidebarLayout ? 1 : undefined,
              minHeight: isSidebarLayout ? 0 : undefined,
            }}
          >
            <Box
              sx={{
                borderRadius: 6,
                padding: 2.5,
                border: `1px solid ${theme.palette.divider}`,
                bgcolor: theme.palette.background.paper,
                display: 'flex',
                flexDirection: 'column',
                gap: 1.5,
                height: '100%',
                width: isVerticalFeed ? '100%' : '320px',
                flexShrink: 0,
                boxSizing: 'border-box',
              }}
            >
              <Box
                sx={{
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  alignItems: 'center',
                  bgcolor: theme.palette.background.default,
                  borderRadius: 4,
                  padding: 3,
                  textAlign: 'center',
                  gap: 1,
                  minHeight: '180px',
                }}
              >
                <Typography variant="subtitle1" color="textPrimary">
                  No updates yet
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Updates from your spaces will appear here.
                </Typography>
              </Box>
            </Box>
          </Box>
        ) : (
          <Box
            sx={{
              minWidth: 0,
              width: '100%',
              overflow: isHorizontalFeed ? 'hidden' : undefined,
              ...(isSidebarLayout
                ? {
                    flex: 1,
                    minHeight: 0,
                    display: 'flex',
                    flexDirection: 'column',
                  }
                : {}),
            }}
          >
            <InfiniteScroller
              items={posts}
              loading={postsLoading}
              hasMore={postsHasMore}
              loadMore={loadMorePosts}
              orientation={isVerticalFeed ? 'vertical' : 'horizontal'}
              height={feedHeight}
              dataTour="home-recent-updates-scroller"
              ariaLabel="Recent updates"
              footer={
                postsLoading && posts.length > 0 ? (
                  <Box
                    display="flex"
                    flexDirection={isVerticalFeed ? 'column' : 'row'}
                    sx={{
                      width: isVerticalFeed ? '100%' : 'auto',
                      height: isVerticalFeed ? 'auto' : '100%',
                    }}
                  >
                    {[0, 1].map((index) => (
                      <Box
                        key={index}
                        role="listitem"
                        sx={{
                          height: isVerticalFeed ? 'auto' : '100%',
                          width: isVerticalFeed ? 'auto' : '320px',
                          mb: 2,
                          mr: isVerticalFeed ? 0 : 2,
                          ...(!isVerticalFeed
                            ? {
                                scrollSnapAlign: 'start',
                                scrollSnapStop: 'always',
                              }
                            : {}),
                        }}
                      >
                        <RecentUpdateCardSkeleton />
                      </Box>
                    ))}
                  </Box>
                ) : null
              }
              renderItem={(item: Post | any) => (
                <Box
                  role="listitem"
                  sx={{
                    height: isVerticalFeed ? 'auto' : '100%',
                    width: isVerticalFeed ? 'auto' : '320px',
                    mb: 2,
                    mr: isVerticalFeed ? 0 : 2,
                    ...(!isVerticalFeed
                      ? {
                          scrollSnapAlign: 'start',
                          scrollSnapStop: 'always',
                        }
                      : {}),
                  }}
                >
                  <RecentUpdateCard
                    key={item?.objectId || item?.id}
                    item={item}
                    onReadMore={(post, scrollTo) => {
                      const query = scrollTo ? `?scrollTo=${scrollTo}` : '';
                      dispatch({
                        type: '@@router/CALL_HISTORY_METHOD',
                        payload: {
                          method: 'push',
                          args: [
                            `/home/post/${post.objectId || post.id}${query}`,
                          ],
                        },
                      } as any);
                    }}
                  />
                </Box>
              )}
            />
          </Box>
        )}
      </Box>
      <RecentUpdatesInfoPopup
        open={isInfoOpen}
        onClose={() => setIsInfoOpen(false)}
      />
      <PostDetailPopup
        item={selectedPost}
        open={!!selectedPost}
        onClose={handleClosePost}
      />
    </Box>
  );
};

export default RecentUpdates;
